import { useState, useCallback } from "react";
import KorektyService from "@/services/KorektyService";

export default function useKorektyData() {
  const defaultLimit = 50;
  const limitIncrement = 50;
  const defaultSortColumn = "invoiceDate";
  const defaultSortDirection = "desc";

  // Data State
  const [data, setData] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination State
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [limit, setLimit] = useState(defaultLimit);
  
  // Filter/Sort State
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({});

  /**
   * Load korekty data using the service
   */
  const loadData = useCallback(async () => {
    if (!loadingMore) setLoading(true);
    setError(null);

    // Use the KorektyService to get korekty with filters
    const result = await KorektyService.getKorektyWithFilters({
      limit,
      sortColumn,
      sortDirection,
      filters: {
        ...filters,
        searchText: searchQuery
      }
    });
    
    if (result.error) {
      setError(result.error);
      setData([]);
      setTotalCount(0);
      setHasMore(false);
    } else {
      setData(result.data);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
    }

    setLoading(false);
    setLoadingMore(false);
  }, [searchQuery, limit, sortColumn, sortDirection, filters, loadingMore]);

  /**
   * Set sorting configuration and trigger reload
   */
  const setSorting = useCallback((column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
    // setLimit(defaultLimit); // Reset limit when sorting changes
  }, []);

  /**
   * Handle search with debouncing effect
   */
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    // setLimit(defaultLimit); // Reset limit when search changes
  }, []);

  /**
   * Load more data for pagination
   */
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const newLimit = limit + limitIncrement;
    setLimit(newLimit);

    const result = await KorektyService.getKorektyWithFilters({
      limit: newLimit,
      sortColumn,
      sortDirection,
      filters: {
        ...filters,
        searchText: searchQuery
      }
    });

    if (result.error) {
      setError(result.error);
    } else {
      setData(result.data);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
    }

    setLoadingMore(false);
  }, [searchQuery, limit, sortColumn, sortDirection, filters, loadingMore, hasMore]);

  /**
   * Update filters and trigger reload
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    // setLimit(defaultLimit); // Reset limit when filters change
  }, []);

  /**
   * Reset all filters and state
   */
  const resetFilters = useCallback(() => {
    setFilters({});
    setSearchQuery("");
    setSortColumn(defaultSortColumn);
    setSortDirection(defaultSortDirection);
    setLimit(defaultLimit);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Data
    data,
    totalCount,
    hasMore,

    // Loading states
    loading,
    loadingMore,
    searching,
    error,

    // Sort/Filter state
    sortColumn,
    sortDirection,
    searchQuery,
    filters,

    // Actions
    loadData,
    handleLoadMore,
    setSorting,
    handleSearch,
    updateFilters,
    resetFilters,
    setError: clearError
  };
}
